import { Alert, Button, Container, Grid, Group, Title } from "@mantine/core";
import { IconCheck, IconCloudDownload, IconDownload, IconStarFilled } from "@tabler/icons-react";
import {
  detectHardwareModel,
  enableDpvrKioskMode,
  getApkVersion,
  grantPermissions,
  installApk,
  pushConfig,
  pushConfig1,
  rebootDevice,
  verifyInstalledVersion,
} from "@utils/webusb-utils/ADBUtils";
import {
  pushesPicoLauncherConfig,
  pushesPicoSeethroughConfig,
} from "@utils/webusb-utils/headsetSetupSteps";
import { HardwareModels } from "types/productTypes";
import React, { useEffect, useRef, useState } from "react";

import DeviceInfoWebUsb from "./DeviceInfoWebUsb";
import DeviceLogsViewer from "./DeviceLogsViewer";
import DownloadProgressBar, { DownloadProgressState } from "./DownloadProgressBar";
import UpdateTimeline from "./UpdateTimeline";
import { compareVersions } from "compare-versions";
import { getVersionStatus } from "@utils/CommonUtil";
import { openConfirmation } from "@actions/actions";
import { openDeviceSetupPage } from "@utils/deviceSetupNavigation";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { downloadReleaseApk } from "@utils/webusb-utils/releaseApkDownload";

function VersionControlWebUsb() {
  const [updateMode, setUpdateMode] = useState(false);
  const [activeStepNo, setActiveStepNo] = useState(0);
  const [timelineTimes, setTimelineTimes] = useState([] as Date[]);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [lastActionDate] = useState(new Date());
  const [osUpdateRequired, setOsUpdateRequired] = useState(false);
  const [appUpdateRequired, setAppUpdateRequired] = useState(false);
  const [offersLookupFailed, setOffersLookupFailed] = useState(false);
  const [availableVersions, setAvailableVersions] = useState([]);
  const [versionData, setVersionData] = useState(null);
  const [adbLogs, setAdbLogs] = useState<string[]>([]);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgressState | null>(null);
  const adbBusyRef = useRef(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const latestApkVersion =
    availableVersions && availableVersions.length > 0 ? availableVersions[0] : null;

  const reportDownloadProgress = (loaded: number, total?: number) => {
    setDownloadProgress({ loaded, total });
  };

  const showDeviceUpdatedModal = () => {
    dispatch(
      openConfirmation(
        t("WebUsb.deviceUpdatedTitle"),
        t("WebUsb.deviceUpdatedMessage"),
        () => {},
        true,
        t("close"),
        "",
      ),
    );
  };

  useEffect(() => {
    if (!deviceInfo) return;
    getVersionStatus(deviceInfo?.version, deviceInfo?.pid).then(
      ({ osUpdateReq, appUpdateReq, versionData, availableVersions, lookupFailed }) => {
        setOsUpdateRequired(osUpdateReq);
        setAppUpdateRequired(appUpdateReq);
        setVersionData(versionData);
        setAvailableVersions(availableVersions);
        setOffersLookupFailed(lookupFailed);
      },
    );
  }, [deviceInfo]);

  async function submitUpdate() {
    if (updateMode || adbBusyRef.current) return;

    // Read APK version directly rather than via populateDeviceInfo, which falls
    // back to the OS version when dumpsys fails and can cause a false positive.
    adbBusyRef.current = true;
    let freshLatestVersion: string | null = null;
    try {
      const installedApkVersion = await getApkVersion();
      const versionStatus = await getVersionStatus(installedApkVersion, deviceInfo?.pid);
      freshLatestVersion = versionStatus.availableVersions?.[0] ?? null;

      if (installedApkVersion && freshLatestVersion && !versionStatus.appUpdateReq) {
        showDeviceUpdatedModal();
        return;
      }
    } catch (e) {
      console.warn("Failed to check device version before update:", e);
    } finally {
      adbBusyRef.current = false;
    }

    setUpdateMode(true);
    setActiveStepNo(0);
    setTimelineTimes((old) => [...old, new Date()]);

    adbBusyRef.current = true;
    try {
      const ver = freshLatestVersion ?? latestApkVersion;
      if (!ver) {
        // No available version: stop update workflow
        setUpdateMode(false);
        setActiveStepNo(-1);
        console.error("No available version to install");
        return;
      }

      const appendAdbLog = (m: string) => setAdbLogs((old) => [...old, m]);

      // 1) Download
      setActiveStepNo(1);
      setTimelineTimes((old) => [...old, new Date()]);
      const model = await detectHardwareModel();
      setDownloadProgress({ loaded: 0 });
      try {
        await downloadReleaseApk(ver, model, appendAdbLog, reportDownloadProgress);
      } finally {
        setDownloadProgress(null);
      }

      setActiveStepNo(2);
      setTimelineTimes((old) => [...old, new Date()]);
      await installApk(undefined, appendAdbLog);

      setActiveStepNo(3);
      setTimelineTimes((old) => [...old, new Date()]);
      await verifyInstalledVersion(ver, appendAdbLog);

      // 4) Push config files
      setActiveStepNo(4);
      setTimelineTimes((old) => [...old, new Date()]);
      if (model === HardwareModels.Dpvr) {
        await enableDpvrKioskMode(appendAdbLog);
      } else if (pushesPicoLauncherConfig(model)) {
        await pushConfig(appendAdbLog);
      }

      // 5) Push Config1
      setActiveStepNo(5);
      setTimelineTimes((old) => [...old, new Date()]);
      if (pushesPicoSeethroughConfig(model)) {
        await pushConfig1(appendAdbLog);
      }
      await grantPermissions(appendAdbLog);

      // finished
      setActiveStepNo(6);
      setTimelineTimes((old) => [...old, new Date()]);

      await rebootDevice(appendAdbLog);
      setUpdateMode(false);
    } catch (err) {
      // on error, stop update mode; consumer components can show logs/errors
      setUpdateMode(false);
      setActiveStepNo(-1);
      console.error(err);
    } finally {
      adbBusyRef.current = false;
    }
  }

  // While an update is running, poll the APK version; when it matches the
  // latest, treat the install as landed and finish the workflow.
  useEffect(() => {
    if (!updateMode || !latestApkVersion) return;
    const intervalId = setInterval(async () => {
      if (adbBusyRef.current) return;
      adbBusyRef.current = true;
      try {
        const apkVer = await getApkVersion();
        if (apkVer && compareVersions(apkVer, latestApkVersion) >= 0) {
          clearInterval(intervalId);
          setUpdateMode(false);
        }
      } catch {
        // ADB mid-operation; retry next tick
      } finally {
        adbBusyRef.current = false;
      }
    }, 5000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateMode, latestApkVersion]);

  const getWebUsbTimelineItems = () => {
    // Timeline steps correspond to submitUpdate steps:
    const items = [
      {
        icon: <IconCloudDownload size={12} />,
        title: t("WebUsb.clickedUpdate"),
        text: t("WebUsb.clickedUpdateText"),
        time: "",
      },
      {
        icon: <IconDownload size={12} />,
        title: t("WebUsb.deviceDownload"),
        text: t("WebUsb.deviceDownloadText"),
        time: "",
      },
      {
        icon: <IconStarFilled size={12} />,
        title: t("WebUsb.installTriggered"),
        text: t("WebUsb.installTriggeredText"),
        time: "",
      },
      {
        icon: <IconCheck size={12} />,
        title: t("WebUsb.verifyInstall"),
        text: t("WebUsb.verifyInstallText"),
        time: "",
      },
      {
        icon: <IconDownload size={12} />,
        title: t("WebUsb.pushConfig"),
        text: t("WebUsb.pushConfigText"),
        time: "",
      },
      {
        icon: <IconDownload size={12} />,
        title: t("WebUsb.pushConfig1"),
        text: t("WebUsb.pushConfig1Text"),
        time: "",
      },
      {
        icon: <IconStarFilled size={12} />,
        title: t("WebUsb.finished"),
        text: t("WebUsb.finishedText"),
        time: "",
      },
    ];

    items.forEach((item, idx) => {
      const time = timelineTimes[idx];
      item.time = time ? time.toLocaleString() : "";
    });
    return items;
  };

  return (
    <Container size={"lg"} px="md" mt="md">
      <Group mb="sm">
        <Title order={3}>{t("UpdatePage")}</Title>
      </Group>

      <Grid mt={"sm"}>
        <Grid.Col span={{ md: 6, sm: 12 }}>
          <DeviceInfoWebUsb
            setDevice={(device) => setDeviceInfo(device)}
            deviceInfoTableProps={{
              osUpdateRequired,
              appUpdateRequired,
              versionData,
              availableVersions,
              submitUpdate: submitUpdate,
            }}
          />
          {offersLookupFailed && (
            <Alert color="red" mt="sm">
              {t("WebUsb.offersLookupFailed")}
            </Alert>
          )}
          {updateMode ? (
            <>
              <UpdateTimeline
                activeStepNo={activeStepNo}
                timelineItems={getWebUsbTimelineItems()}
              />
              {downloadProgress && <DownloadProgressBar {...downloadProgress} />}
            </>
          ) : null}
          <Group mt="md">
            <Button variant="subtle" onClick={() => openDeviceSetupPage(navigate)}>
              {t("VersionControl.recoveryPrompt")}
            </Button>
          </Group>
        </Grid.Col>
        {deviceInfo ? (
          <Grid.Col span={{ md: 6, sm: 12 }}>
            <DeviceLogsViewer
              pid={deviceInfo?.pid}
              setActiveStepNo={setActiveStepNo}
              timeOffset={lastActionDate}
              setUpdateTimelineTimes={setTimelineTimes}
              adbLogs={adbLogs}
            />
          </Grid.Col>
        ) : null}
      </Grid>
    </Container>
  );
}

export default VersionControlWebUsb;
